import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PORTAL_URL } from '../../content/site';
import styles from './PortalSelector.module.css';

const OPTIONS = [
  { label: 'VIC / NSW', name: 'Victoria & New South Wales', destination: PORTAL_URL },
  { label: 'QLD / SA', name: 'Queensland & South Australia', destination: 'https://portal.smokealarmsolutions.com.au/' },
];

const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export default function PortalSelector({ className, children, onSelect }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const id = useId();

  useClientLayoutEffect(() => {
    if (!open) return undefined;
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return undefined;
    const margin = 12;
    const gap = 4;
    let previousGeometry = '';
    let frame;
    const placeMenu = () => {
      const anchor = trigger.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || window.innerWidth;
      const viewportHeight = viewport?.height || window.innerHeight;
      const viewportRight = viewportLeft + viewportWidth;
      const viewportBottom = viewportTop + viewportHeight;
      // Dismiss when the trigger has scrolled out of view rather than leaving
      // the dropdown floating against the edge of the screen.
      if (anchor.bottom <= viewportTop || anchor.top >= viewportBottom || anchor.right <= viewportLeft || anchor.left >= viewportRight) {
        setOpen(false);
        return;
      }
      const width = Math.min(anchor.width, viewportWidth - margin * 2);
      const widthValue = width + 'px';
      if (menu.style.width !== widthValue) menu.style.width = widthValue;
      // offsetHeight is independent of the opening animation's transform.
      const height = menu.offsetHeight;
      const belowSpace = viewportBottom - anchor.bottom - gap - margin;
      const aboveSpace = anchor.top - viewportTop - gap - margin;
      const above = belowSpace < height && aboveSpace > belowSpace;
      const available = Math.max(0, above ? aboveSpace : belowSpace);
      const top = above ? anchor.top - Math.min(height, available) - gap : anchor.bottom + gap;
      const left = Math.max(viewportLeft + margin, Math.min(anchor.right - width, viewportRight - width - margin));
      const geometry = [top, left, width, available, above].join(',');
      if (geometry !== previousGeometry) {
        menu.style.top = top + 'px';
        menu.style.left = left + 'px';
        menu.style.maxHeight = available + 'px';
        menu.dataset.placement = above ? 'above' : 'below';
        menu.style.visibility = 'visible';
        previousGeometry = geometry;
      }
    };
    // Follow layout changes as well as scroll events, including a header
    // becoming sticky. Direct DOM positioning avoids a delayed React render.
    const followAnchor = () => {
      placeMenu();
      frame = window.requestAnimationFrame(followAnchor);
    };
    followAnchor();
    menu.querySelector('a')?.focus({ preventScroll: true });
    const dismissOutside = (event) => {
      if (menu.contains(event.target) || trigger.contains(event.target)) return;
      setOpen(false);
    };
    const dismissEscape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      trigger.focus({ preventScroll: true });
    };
    document.addEventListener('pointerdown', dismissOutside, true);
    document.addEventListener('focusin', dismissOutside);
    document.addEventListener('keydown', dismissEscape, true);
    window.addEventListener('scroll', placeMenu, true);
    window.addEventListener('resize', placeMenu);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', dismissOutside, true);
      document.removeEventListener('focusin', dismissOutside);
      document.removeEventListener('keydown', dismissEscape, true);
      window.removeEventListener('scroll', placeMenu, true);
      window.removeEventListener('resize', placeMenu);
    };
  }, [open]);

  return <>
    <button
      ref={triggerRef}
      type="button"
      className={className}
      style={{ appearance: 'none', cursor: 'pointer' }}
      aria-expanded={open}
      aria-controls={id}
      onClick={() => setOpen((wasOpen) => !wasOpen)}
    >
      {children}
    </button>
    {open && createPortal(
      <section
        ref={menuRef}
        id={id}
        className={styles.menu}
        aria-labelledby={id + '-title'}
      >
        <h2 id={id + '-title'}>Select your state</h2>
        <div className={styles.options}>
          {OPTIONS.map(({ label, name, destination }) => (
            <a
              key={label}
              className={styles.option}
              href={destination}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={name + ' (opens in a new tab)'}
              onClick={() => { onSelect?.(); setOpen(false); }}
            >
              {label}
            </a>
          ))}
        </div>
      </section>, document.body,
    )}
  </>;
}
